import { Icon } from "@iconify/react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Tooltip as MuiTooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import html2canvas from "html2canvas";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LuChevronDown } from "react-icons/lu";
import Select from "react-select";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Label,
  LabelList,
  Legend,
  Line,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import CinematicShowcase from "../../components/custom/CinematicShowcase";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import { Building } from "../../db_access/building";
import muiStyle from "../../style/components-style/muiStyle";
import "../../style/pages-style/Comparison.css";

// --- CONSTANTS & HELPERS ---
const COLORS = ["#16a34a", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316"];
const METRICS_LEGEND = [
  { key: "gwp", label: "Total Carbon", icon: "Carbon" },
  { key: "intensity", label: "Intensity", icon: "Goal" },
  { key: "embodied", label: "Structural Carbon", icon: "Shell" },
  { key: "operational", label: "Usage Carbon", icon: "Power" },
  { key: "area", label: "Efficiency", icon: "Area" },
];

/**
 * Enhanced formatter that converts to Tons (t) for values >= 1000 kg
 */
const formatValueRaw = (kg) => {
  if (kg === undefined || kg === null) return "0";
  const abs = Math.abs(kg);
  if (abs >= 1000) {
    return (kg / 1000).toFixed(2) + " t";
  }
  return Math.round(kg).toLocaleString() + " kg";
};

// Tooltip specifically for chart bars
const CustomTooltip = ({ active, payload, label }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  if (active && payload && payload.length) {
    return (
      <div
        className="custom-tooltip"
        style={{
          backgroundColor: isDark ? "#27272a" : "white",
          padding: "16px",
          border: `1px solid ${isDark ? "#3f3f46" : "#e2e8f0"}`,
          borderRadius: "8px",
          boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.5)" : "0 4px 12px rgba(0,0,0,0.1)",
          minWidth: "180px",
        }}>
        <p
          className="tooltip-label"
          style={{
            fontFamily: "Poppins",
            fontWeight: 700,
            marginBottom: "8px",
            color: isDark ? "#f8fafc" : "#1e293b",
            borderBottom: `1px solid ${isDark ? "#3f3f46" : "#f1f5f9"}`,
            paddingBottom: "4px",
          }}>
          {label}
        </p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color || p.fill, fontSize: "12px", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: p.color || p.fill }}></span>
              {p.name}:
            </span>
            <span style={{ fontWeight: 600, color: isDark ? "#e4e4e7" : "inherit" }}>{formatValueRaw(p.value)}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// --- VRTTA SCORE ENGINE (Baseline-Anchored Log Model v2.1) ---

const clamp = (min, max, v) => Math.max(min, Math.min(max, v));

/**
 * Maps performance ratio to a score using Symmetric Log Distance.
 * Baseline (ratio 1.0) = 75.
 */
const scoreLogDistance = (target, baseline, slope = 25) => {
  const ratio = target / Math.max(baseline, 1e-6);
  const distance = Math.log2(ratio); // baseline = 0, half = -1, double = 1
  return clamp(0, 100, 75 - distance * slope);
};

/**
 * Derives uncertainty from material-level metadata (Integrity Signals)
 * Uses envFactorOptions master database that's already loaded in the frontend
 */
const materialUncertainty = (m, envFactorOptions = {}) => {
  // Try to get uncertainty from the material object itself first
  if (m.uncertainty !== undefined && m.uncertainty !== null && m.uncertainty !== "") {
    const raw = parseFloat(m.uncertainty);
    const normalized = raw >= 1 ? raw / 100 : raw; // Fixed: Use >= instead of >
    return {
      score: clamp(0.05, 0.8, normalized),
      raw: normalized,
      sourceValue: raw,
      flags: [`Material DB: ${(normalized * 100).toFixed(2)}%`], // Clean display
    };
  }

  // Look up in envFactorOptions master database
  // Structure: envFactorOptions[type][category][sub_category][description]
  const materialName = (m.material || m.Description || m.description || m.name || "").trim();
  const type = m.type || "";
  const category = m.category || "";
  const sub_category = m.sub_category || "";

  // Try exact lookup first if we have all metadata
  if (type && category && materialName && envFactorOptions) {
    const matchedArray = envFactorOptions?.[type]?.[category]?.[sub_category]?.[materialName];
    if (matchedArray && Array.isArray(matchedArray) && matchedArray.length > 0) {
      const masterRecord = matchedArray[0];
      const uValue = masterRecord.uncertainty || masterRecord.uncer || masterRecord.statistical || masterRecord.dist_param || masterRecord.uc;

      if (uValue !== undefined && uValue !== null && uValue !== "") {
        const raw = parseFloat(uValue);
        const normalized = raw >= 1 ? raw / 100 : raw;
        return {
          score: clamp(0.05, 0.8, normalized),
          raw: normalized,
          sourceValue: raw,
          flags: [`Master DB: ${(normalized * 100).toFixed(2)}%`],
        };
      }
    }
  }

  // Fallback: Search by material name only (when type/category/sub_category not available)
  if (materialName && envFactorOptions) {
    for (const typeKey of Object.keys(envFactorOptions || {})) {
      for (const catKey of Object.keys(envFactorOptions[typeKey] || {})) {
        for (const subKey of Object.keys(envFactorOptions[typeKey][catKey] || {})) {
          const descObj = envFactorOptions[typeKey][catKey][subKey];
          // Check if this description matches
          if (descObj[materialName] && Array.isArray(descObj[materialName]) && descObj[materialName].length > 0) {
            const masterRecord = descObj[materialName][0];
            const uValue = masterRecord.uncertainty || masterRecord.uncer || masterRecord.statistical || masterRecord.dist_param || masterRecord.uc;

            if (uValue !== undefined && uValue !== null && uValue !== "") {
              const raw = parseFloat(uValue);
              const normalized = raw >= 1 ? raw / 100 : raw;
              return {
                score: clamp(0.05, 0.8, normalized),
                raw: normalized,
                sourceValue: raw,
                flags: [`Master DB: ${(normalized * 100).toFixed(2)}%`],
              };
            }
          }
        }
      }
    }
  }

  // Default to clean 20% base if no uncertainty field is found
  return { score: 0.2, raw: 0.2, sourceValue: 20, flags: ["Default: 20%"] };
};

/**
 * Project-level uncertainty weighted by carbon impact
 */
const getProjectUncertainty = (materials, options = {}) => {
  let total = 0,
    weighted = 0;
  materials.forEach((m) => {
    const impact = m.impact || parseFloat(m.quantity || 0) * parseFloat(m.gwp_per_unit || m["GWP per unit (kg CO2e)"] || 0);
    const { score } = materialUncertainty(m, options);
    total += impact;
    weighted += impact * score;
  });
  return total > 0 ? weighted / total : 0.8;
};

const calculateVrttaScore = (target, baseline, options = {}, returnBreakdown = false, providedMaterials = null) => {
  if (!target || !baseline) return returnBreakdown ? {} : 0;

  // 1. Performance Calculation (Log-Distance from Baseline)
  const sInt = scoreLogDistance(target.intensity, baseline.intensity, 25);

  const tEmbInt = target.embodied / (target.area || 1);
  const bEmbInt = baseline.embodied / (baseline.area || 1);
  const sEmb = scoreLogDistance(tEmbInt, bEmbInt, 18);

  const stagesAudited = target.data?.lcaGroups?.filter((g) => Math.abs(g.value) > 0).length || 0;
  const sAud = (stagesAudited / 6) * 100;

  const raw = sInt * 0.5 + sEmb * 0.25 + sAud * 0.25;

  // 2. Confidence Adjustment (Deriving Uncertainty from 100% Material Lineage)
  const materials = providedMaterials || target.materials || target.data?.materials || [];
  const uncertainty = getProjectUncertainty(materials, options);
  const confidence = clamp(0.6, 1.0, 1 - uncertainty);

  const final = Math.round(clamp(5, 99, raw * confidence));

  if (returnBreakdown) {
    return {
      final,
      raw,
      confidence,
      uncertainty,
      sInt,
      sEmb,
      sAud,
      inputs: {
        targetIntensity: target.intensity,
        baselineIntensity: baseline.intensity,
        targetEmb: tEmbInt,
        baselineEmb: bEmbInt,
        stages: stagesAudited,
        matCount: materials.length,
      },
    };
  }
  return final;
};

const DataSourceAudit = ({ m, baseline, envFactorOptions }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const materials = m.materials || [];
  const b = calculateVrttaScore(m, baseline, envFactorOptions, true, materials);

  return (
    <Box
      sx={{
        mt: 3,
        p: 4,
        bgcolor: isDark ? "#1c1c1e" : "#ffffff",
        border: `1px solid ${isDark ? "#3f3f46" : "#e2e8f0"}`,
        borderRadius: 4,
        boxShadow: isDark ? "0 10px 15px -3px rgb(0 0 0 / 0.4)" : "0 10px 15px -3px rgb(0 0 0 / 0.1)",
        animation: "fade-in 0.4s ease",
      }}>
      <Typography sx={{ ...muiStyle.typographyTableTitle, fontSize: 18, color: "#10b981", mb: 3, textAlign: "center", textTransform: "uppercase", letterSpacing: 2 }}>
        Scientific Data Lineage Audit (v2.2)
      </Typography>

      {/* Pillar 1: Source Mapping */}
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 800,
          color: isDark ? "#e4e4e7" : "#1e293b",
          mb: 2,
          textTransform: "uppercase",
          borderLeft: "4px solid #10b981",
          pl: 1,
          fontFamily: "Poppins",
        }}>
        Section 1: Data Origin & Normalization
      </Typography>
      <TableContainer sx={{ mb: 4, border: `1px solid ${isDark ? "#3f3f46" : "#f1f5f9"}`, borderRadius: 2 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: isDark ? "#27272a" : "#f8fafc" }}>
            <TableRow>
              <TableCell sx={{ fontSize: 11, fontWeight: 900, color: isDark ? "#a1a1aa" : "#64748b", fontFamily: "Poppins", borderBottomColor: isDark ? "#3f3f46" : "#f1f5f9" }}>
                AUDIT STEP
              </TableCell>
              <TableCell sx={{ fontSize: 11, fontWeight: 900, color: isDark ? "#a1a1aa" : "#64748b", fontFamily: "Poppins", borderBottomColor: isDark ? "#3f3f46" : "#f1f5f9" }}>
                DATABASE SOURCE
              </TableCell>
              <TableCell
                sx={{ fontSize: 11, fontWeight: 900, color: isDark ? "#a1a1aa" : "#64748b", fontFamily: "Poppins", borderBottomColor: isDark ? "#3f3f46" : "#f1f5f9" }}
                align="right">
                SUBSTITUTED VALUE
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontSize: 12, fontWeight: 600, fontFamily: "Poppins", color: isDark ? "#f8fafc" : "inherit", borderBottomColor: isDark ? "#3f3f46" : "#f1f5f9" }}>
                Raw GWP Extraction
              </TableCell>
              <TableCell sx={{ fontSize: 11, color: isDark ? "#a1a1aa" : "#64748b", fontFamily: "Poppins", borderBottomColor: isDark ? "#3f3f46" : "#f1f5f9" }}>
                Table: buildings_audit_v2
              </TableCell>
              <TableCell sx={{ fontSize: 13, fontWeight: 800, color: "#10b981", fontFamily: "Poppins", borderBottomColor: isDark ? "#3f3f46" : "#f1f5f9" }} align="right">
                {(m.gwp / 1000).toFixed(2)} t CO2e
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontSize: 12, fontWeight: 600, fontFamily: "Poppins", color: isDark ? "#f8fafc" : "inherit", borderBottomColor: isDark ? "#3f3f46" : "#f1f5f9" }}>
                Functional Area Mapping
              </TableCell>
              <TableCell sx={{ fontSize: 11, color: isDark ? "#a1a1aa" : "#64748b", fontFamily: "Poppins", borderBottomColor: isDark ? "#3f3f46" : "#f1f5f9" }}>
                Field: builtup_area
              </TableCell>
              <TableCell
                sx={{ fontSize: 13, fontWeight: 800, fontFamily: "Poppins", color: isDark ? "#f8fafc" : "inherit", borderBottomColor: isDark ? "#3f3f46" : "#f1f5f9" }}
                align="right">
                {m.area.toLocaleString()} m²
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontSize: 12, fontWeight: 600, fontFamily: "Poppins", color: isDark ? "#f8fafc" : "inherit", borderBottomColor: isDark ? "#3f3f46" : "#f1f5f9" }}>
                Carbon Intensity (KPI)
              </TableCell>
              <TableCell sx={{ fontSize: 11, color: isDark ? "#a1a1aa" : "#64748b", fontFamily: "Poppins", borderBottomColor: isDark ? "#3f3f46" : "#f1f5f9" }}>
                Derived Calculation
              </TableCell>
              <TableCell
                sx={{ fontSize: 13, fontWeight: 800, fontFamily: "Poppins", color: isDark ? "#f8fafc" : "inherit", borderBottomColor: isDark ? "#3f3f46" : "#f1f5f9" }}
                align="right">
                {m.intensity.toFixed(2)} kg/m²
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pillar 2: Material Level Audit */}
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 800,
          color: isDark ? "#e4e4e7" : "#1e293b",
          mb: 2,
          textTransform: "uppercase",
          borderLeft: "4px solid #10b981",
          pl: 1,
          fontFamily: "Poppins",
        }}>
        Section 2: Material Inventory & Uncertainty Trace
      </Typography>
      <TableContainer sx={{ border: `1px solid ${isDark ? "#3f3f46" : "#f1f5f9"}`, borderRadius: 2, maxHeight: 400, overflow: "auto" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontSize: 11,
                  fontWeight: 900,
                  color: isDark ? "#a1a1aa" : "#64748b",
                  fontFamily: "Poppins",
                  bgcolor: isDark ? "#27272a" : "#f8fafc",
                  borderBottomColor: isDark ? "#3f3f46" : "#f1f5f9",
                }}>
                MATERIAL IDENTIFIER
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 11,
                  fontWeight: 900,
                  color: isDark ? "#a1a1aa" : "#64748b",
                  fontFamily: "Poppins",
                  bgcolor: isDark ? "#27272a" : "#f8fafc",
                  borderBottomColor: isDark ? "#3f3f46" : "#f1f5f9",
                }}
                align="right">
                IMPACT (kg)
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 11,
                  fontWeight: 900,
                  color: isDark ? "#a1a1aa" : "#64748b",
                  fontFamily: "Poppins",
                  bgcolor: isDark ? "#27272a" : "#f8fafc",
                  borderBottomColor: isDark ? "#3f3f46" : "#f1f5f9",
                }}
                align="right">
                AUDIT FLAGS
              </TableCell>
              <TableCell
                sx={{
                  fontSize: 11,
                  fontWeight: 900,
                  color: isDark ? "#a1a1aa" : "#64748b",
                  fontFamily: "Poppins",
                  bgcolor: isDark ? "#27272a" : "#f8fafc",
                  borderBottomColor: isDark ? "#3f3f46" : "#f1f5f9",
                }}
                align="right">
                UNCERTAINTY ($U_p$)
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {materials.map((mat, idx) => {
              const { raw, flags } = materialUncertainty(mat, envFactorOptions);
              const impact = mat.impact || parseFloat(mat.quantity || 0) * parseFloat(mat.gwp_per_unit || mat["GWP per unit (kg CO2e)"] || 0);

              return (
                <TableRow key={idx} sx={{ "&:hover": { bgcolor: isDark ? "#27272a" : "#f1f5f9" }, transition: "background 0.2s" }}>
                  <TableCell
                    sx={{ fontSize: 12, fontWeight: 500, fontFamily: "Poppins", color: isDark ? "#f8fafc" : "inherit", borderBottomColor: isDark ? "#3f3f46" : "#f1f5f9" }}>
                    {mat.material || mat.name || mat.Description || "Unknown Material"}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: isDark ? "#e4e4e7" : "#1e293b", fontFamily: "Poppins", borderBottomColor: isDark ? "#3f3f46" : "#f1f5f9" }} align="right">
                    {Math.round(impact).toLocaleString()}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: 10,
                      color: flags.includes("Verified DB Value") ? "#10b981" : isDark ? "#a1a1aa" : "#64748b",
                      fontWeight: 700,
                      fontFamily: "Poppins",
                      borderBottomColor: isDark ? "#3f3f46" : "#f1f5f9",
                    }}
                    align="right">
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
                      {flags.includes("Verified DB Value") ? <Icon icon="lucide:check-circle" width="14" /> : <Icon icon="lucide:info" width="14" />}
                      {flags[0]}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{ fontSize: 12, fontWeight: 700, color: raw > 0.4 ? "#ef4444" : "#10b981", fontFamily: "Poppins", borderBottomColor: isDark ? "#3f3f46" : "#f1f5f9" }}
                    align="right">
                    {(raw * 100).toFixed(0)}%
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow sx={{ bgcolor: isDark ? "rgba(16, 185, 129, 0.1)" : "#f1fdf4", position: "sticky", bottom: 0, zIndex: 1, borderTop: "2px solid #10b981" }}>
              <TableCell colSpan={3} sx={{ fontSize: 12, fontWeight: 800, fontFamily: "Poppins", color: isDark ? "#f8fafc" : "inherit" }}>
                PROJECT WEIGHTED AVERAGE UNCERTAINTY
              </TableCell>
              <TableCell sx={{ fontSize: 14, fontWeight: 900, color: "#10b981", fontFamily: "Poppins" }} align="right">
                {(b.uncertainty * 100).toFixed(1)}%
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pillar 3: Scientific Verification Section */}
      <Box sx={{ mt: 4, p: 3, bgcolor: isDark ? "#27272a" : "#f8fafc", borderRadius: 3, border: `1px solid ${isDark ? "#3f3f46" : "#e2e8f0"}` }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: isDark ? "#f8fafc" : "#1e293b", mb: 1, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 1 }}>
          <Icon icon="lucide:microscope" /> Scientific Proof & Verification Statement
        </Typography>
        <Typography sx={{ fontSize: 12, color: isDark ? "#a1a1aa" : "#64748b", lineHeight: 1.6, textAlign: "justify" }}>
          The **VRTTA Intelligence Engine (v2.2)** has completed a direct database trace for **{m.name}**. A total of **{materials.length} material line items** were audited. The
          engine strictly extracted **Statistical Uncertainty Factors** found within your database or the Global Environmental Factor Master Database. Calculations reflect the pure
          uncertainty values provided at the source, without heuristic penalties for regional or method metadata. The final weighted uncertainty for this project is **
          {(b.uncertainty * 100).toFixed(1)}%**, resulting in a **Scientific Confidence Factor of {b.confidence.toFixed(2)}x**.
        </Typography>
      </Box>
    </Box>
  );
};

const AuditTrace = ({ data }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { final, raw, confidence, uncertainty, sInt, sEmb, sAud, inputs } = data;

  const FormulaRow = ({ label, formula, substitution, result, color = "#1e293b" }) => (
    <Box sx={{ mb: 2, p: 2, bgcolor: isDark ? "#27272a" : "#f8fafc", borderRadius: 2, borderLeft: `4px solid ${color}` }}>
      <Typography sx={{ fontSize: 10, fontWeight: 900, color: isDark ? "#71717a" : "#94a3b8", mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontFamily: "monospace", fontWeight: 600, color: isDark ? "#a1a1aa" : "#64748b", mb: 0.5 }}>{formula}</Typography>
      <Typography sx={{ fontSize: 12, color: isDark ? "#e4e4e7" : "#1e293b", fontStyle: "italic", mb: 1 }}>{substitution}</Typography>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Typography sx={{ fontSize: 14, fontWeight: 900, color }}>Result: {Math.round(result)}%</Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ mt: 2, animation: "fade-in 0.5s ease" }}>
      <Typography sx={{ fontSize: 12, fontWeight: 800, mb: 2, color: "#10b981", letterSpacing: 1 }}>VRTTA CALCULATION TRACE (v2.1)</Typography>

      <FormulaRow
        label="PILLAR 1: INTENSITY SCORE"
        formula="75 - (log2(Target/Baseline) * 25)"
        substitution={`75 - (log2(${inputs.targetIntensity.toFixed(1)} / ${inputs.baselineIntensity.toFixed(1)}) * 25)`}
        result={sInt}
        color="#3b82f6"
      />

      <FormulaRow
        label="PILLAR 2: EMBODIED SCORE"
        formula="75 - (log2(TargetEmb/BaselineEmb) * 18)"
        substitution={`75 - (log2(${inputs.targetEmb.toFixed(1)} / ${inputs.baselineEmb.toFixed(1)}) * 18)`}
        result={sEmb}
        color="#8b5cf6"
      />

      <FormulaRow label="PILLAR 3: AUDIT DEPTH" formula="(StagesAudited / 6) * 100" substitution={`(${inputs.stages} / 6) * 100`} result={sAud} color="#10b981" />

      <Box sx={{ p: 2, mt: 3, bgcolor: isDark ? "#27272a" : "#f1f5f9", borderRadius: 2 }}>
        <Typography sx={{ fontSize: 10, fontWeight: 900, mb: 1, color: isDark ? "#71717a" : "#64748b" }}>CONFIDENCE ADJUSTMENT</Typography>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography sx={{ fontSize: 12, color: isDark ? "#e4e4e7" : "inherit" }}>Weighted Uncertainty ($U_p$)</Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: isDark ? "#f8fafc" : "inherit" }}>{(uncertainty * 100).toFixed(1)}%</Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontSize: 12, color: isDark ? "#e4e4e7" : "inherit" }}>Multiplier ($C = 1 - U_p$)</Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: isDark ? "#f8fafc" : "inherit" }}>{confidence.toFixed(2)}x</Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 3, p: 2, bgcolor: isDark ? "#09090b" : "#1e293b", borderRadius: 2, color: "white", textAlign: "center" }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, opacity: 0.7 }}>FINAL AGGREGATION</Typography>
        <Typography sx={{ fontSize: 14, my: 1, color: "#e4e4e7" }}>
          Score = Round( (0.5 * {Math.round(sInt)} + 0.25 * {Math.round(sEmb)} + 0.25 * {Math.round(sAud)}) * {confidence.toFixed(2)} )
        </Typography>
        <Typography sx={{ fontSize: 24, fontWeight: 900, color: "#10b981" }}>{final} / 99</Typography>
      </Box>
    </Box>
  );
};

const SectionAccordion = ({ title, children, defaultExpanded = true, icon }) => {
  return (
    <Accordion defaultExpanded={defaultExpanded} sx={{ ...muiStyle.accordion, mb: 3 }} elevation={0} disableGutters>
      <AccordionSummary expandIcon={<LuChevronDown size={20} color="#64748b" />} sx={muiStyle.accordionSummary}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 3 }}>
          {icon && <Box sx={{ color: "#10b981", display: "flex" }}>{icon}</Box>}
          <Typography sx={{ ...muiStyle.accordionSummaryTypography, px: 0 }}>{title}</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ ...muiStyle.accordionDetails, padding: 3 }}>{children}</AccordionDetails>
    </Accordion>
  );
};

const SectionHeader = ({ title, subtitle, info }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontWeight: 700, fontSize: 18, color: isDark ? "#f8fafc" : "#1e293b" }}>{title}</Typography>
        {info && (
          <MuiTooltip title={info}>
            <IconButton size="small">
              <Icon icon="lucide:info" width="16" color={isDark ? "#94a3b8" : "inherit"} />
            </IconButton>
          </MuiTooltip>
        )}
      </Box>
      {subtitle && <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 13, color: isDark ? "#a1a1aa" : "#64748b", mt: 0.5 }}>{subtitle}</Typography>}
    </Box>
  );
};

// --- MAIN COMPONENT ---

function BuildingComparison() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const mode = theme.palette.mode;

  const { envFactorOptions } = useContextProvider();
  const [loading, setLoading] = useState(false);
  const [allBuildings, setAllBuildings] = useState([]);
  const [comparisonData, setComparisonData] = useState({});

  const [primaryId, setPrimaryId] = useState(null);
  const [secondaryIds, setSecondaryIds] = useState([]);
  const [showTrace, setShowTrace] = useState(null);
  const [showDataAudit, setShowDataAudit] = useState(null);

  const dashboardRef = useRef(null);

  // 1. Fetch Basic List
  useEffect(() => {
    Building.get_mine()
      .then((res) => res.json())
      .then((data) => {
        const formatted = data.map((b) => ({ value: b.id, label: b.name, ...b }));
        setAllBuildings(formatted);
      })
      .catch((err) => console.error("Failed to load buildings list", err));
  }, []);

  // 2. Fetch Detailed Data
  useEffect(() => {
    const idsToFetch = [primaryId, ...secondaryIds].filter(Boolean);
    if (idsToFetch.length === 0) {
      setComparisonData({});
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const promises = idsToFetch.map((id) =>
          Building.get_results({
            building_id: id,
            filter_category: "Global",
            filter_subcategory: null,
            is_comparison: true, // Load all materials with uncertainty
          }).then((res) => res.json().then((data) => ({ id, data })))
        );

        const results = await Promise.all(promises);
        const newMap = {};
        results.forEach(({ id, data }) => {
          newMap[id] = data;
        });
        setComparisonData(newMap);
      } catch (err) {
        console.error("Error fetching comparison details", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [primaryId, secondaryIds]);

  // --- HANDLERS ---
  const handleExport = async () => {
    if (dashboardRef.current) {
      const canvas = await html2canvas(dashboardRef.current, { scale: 2, useCORS: true });
      const link = document.createElement("a");
      link.download = `VRTTA_Comparison_${new Date().toISOString().slice(0, 10)}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    }
  };

  // --- DATA PROCESSING HOOKS ---

  const processedMetrics = useMemo(() => {
    const ids = [primaryId, ...secondaryIds].filter((id) => id && comparisonData[id]);
    return ids.map((id, index) => {
      const d = comparisonData[id];
      const meta = allBuildings.find((b) => b.id === id) || {};

      const embodied = d?.embodiedCarbon || d?.metrics?.embodiedCarbon || 0;
      const operational = d?.operationalCarbon || d?.metrics?.operationalCarbon || 0;
      const transport = d?.transportCarbon || 0;
      const total = embodied + operational + transport;
      const area = meta.gross_floor_area || meta.builtup_area || meta.area || d?.building?.builtup_area || d?.metadata?.gross_floor_area || 1;

      // Use materials from API (now includes uncertainty)
      const materials = d?.materials || [];
      const topMaterial = materials.length > 0 ? [...materials].sort((a, b) => b.impact - a.impact)[0] : { material: "N/A", pct: 0 };

      // Extract sections sum
      const sections = (d?.buildingSections || []).map((s) => ({
        section: s.category.charAt(0).toUpperCase() + s.category.slice(1).toLowerCase(),
        impact: s.subs.reduce((acc, curr) => acc + Math.abs(curr.impact), 0),
      }));

      return {
        id,
        name: meta.name || "Unknown",
        color: COLORS[index % COLORS.length],
        gwp: total,
        embodied,
        operational,
        transport,
        intensity: area > 0 ? total / area : total,
        year: meta.year || new Date().getFullYear(),
        area,
        topMaterial,
        sections,
        materials,
        data: d,
      };
    });
  }, [comparisonData, primaryId, secondaryIds, allBuildings]);

  // Strategic Insights Engine
  const smartInsights = useMemo(() => {
    if (processedMetrics.length < 2) return [];
    const insights = [];
    const baseline = processedMetrics[0];
    const challenger = processedMetrics[1];

    // 1. Efficiency Insight
    if (challenger.intensity < baseline.intensity) {
      insights.push({
        type: "success",
        text: `${challenger.name} is ${Math.round(((baseline.intensity - challenger.intensity) / baseline.intensity) * 100)}% more carbon-efficient per m² than ${baseline.name}.`,
        icon: "lucide:check-circle",
      });
    } else {
      insights.push({
        type: "warning",
        text: `${challenger.name} has a ${Math.round(((challenger.intensity - baseline.intensity) / baseline.intensity) * 100)}% higher carbon intensity than ${baseline.name}.`,
        icon: "lucide:alert-circle",
      });
    }

    return insights;
  }, [processedMetrics]);

  // Radar Data (Normalized Sustainability Score)
  const radarData = useMemo(() => {
    if (processedMetrics.length === 0) return [];
    return METRICS_LEGEND.map((metric) => {
      const point = { metric: metric.label, fullMark: 100 };
      const maxValueInSet = Math.max(...processedMetrics.map((m) => m[metric.key] || 0));
      const normalizationBase = maxValueInSet > 0 ? maxValueInSet : 1;
      processedMetrics.forEach((m) => {
        point[m.name] = ((m[metric.key] || 0) / normalizationBase) * 100;
      });
      return point;
    });
  }, [processedMetrics]);

  // Carbon Gap Analysis (Improved Variance)
  const varianceData = useMemo(() => {
    if (processedMetrics.length < 2) return [];
    const baseline = processedMetrics[0];
    const challengers = processedMetrics.slice(1);
    const stages = ["A1-A3", "A4-A5", "B1-B5", "B6-B7", "C1-C4", "D"];

    return stages.map((stage) => {
      const row = { name: stage };
      const baselineVal = Math.abs(baseline.data?.lcaGroups?.find((s) => s.stage_group === stage)?.value || 0);
      challengers.forEach((chal) => {
        const chalVal = Math.abs(chal.data?.lcaGroups?.find((s) => s.stage_group === stage)?.value || 0);
        row[chal.name] = chalVal - baselineVal;
      });
      return row;
    });
  }, [processedMetrics]);

  // Sectional Comparison Data (Fixed)
  const sectionalData = useMemo(() => {
    if (processedMetrics.length === 0) return [];
    const allSections = new Set();
    processedMetrics.forEach((m) => {
      m.sections.forEach((s) => allSections.add(s.section));
    });

    return Array.from(allSections).map((secName) => {
      const row = { name: secName };
      processedMetrics.forEach((m) => {
        const found = m.sections.find((x) => x.section === secName);
        row[m.name] = found ? found.impact : 0;
      });
      return row;
    });
  }, [processedMetrics]);

  // Material Comparison Data + Percentages
  const materialData = useMemo(() => {
    const allMats = new Set();
    processedMetrics.forEach((m) => {
      (m.data?.materials || []).slice(0, 8).forEach((mat) => allMats.add(mat.material));
    });

    return Array.from(allMats).map((matName) => {
      const row = { name: matName.length > 30 ? matName.substring(0, 30) + "..." : matName };
      processedMetrics.forEach((m) => {
        const found = m.data?.materials?.find((x) => x.material === matName);
        const impact = found ? found.impact : 0;
        row[m.name] = impact;
        row[`${m.name}_pct`] = found ? found.pct : 0;
        row[`${m.name}_val`] = formatValueRaw(impact);
      });
      return row;
    });
  }, [processedMetrics]);

  // Hotspot Concentration (Pareto Curve) Data
  const paretoData = useMemo(() => {
    if (processedMetrics.length === 0) return [];
    // We'll track the top 15 material contributors as that's where 99% of hotspots live
    const dataPoints = Array.from({ length: 15 }, (_, i) => ({ rank: `Mat ${i + 1}`, index: i + 1 }));

    processedMetrics.forEach((m) => {
      const materials = [...(m.data?.materials || [])].sort((a, b) => b.impact - a.impact);
      const totalImpact = m.embodied || 1;
      let cumulative = 0;

      dataPoints.forEach((pt, idx) => {
        if (idx < materials.length) {
          cumulative += materials[idx].impact;
        }
        pt[m.name] = Math.min((cumulative / totalImpact) * 100, 100);
      });
    });
    return dataPoints;
  }, [processedMetrics]);

  // Pareto Analysis Insights
  const paretoInsights = useMemo(() => {
    return processedMetrics.map((m) => {
      const materials = [...(m.data?.materials || [])].sort((a, b) => b.impact - a.impact);
      const totalImpact = m.embodied || 1;
      let sum = 0;
      let count80 = 0;
      for (let i = 0; i < materials.length; i++) {
        sum += materials[i].impact;
        count80++;
        if (sum / totalImpact >= 0.8) break;
      }
      return { name: m.name, count80, color: m.color };
    });
  }, [processedMetrics]);

  return (
    <Box sx={muiStyle.mainContainer} ref={dashboardRef}>
      {/* --- TOP BAR --- */}
      <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box>
          <Typography sx={{ ...muiStyle.typographyPageTitle, color: isDark ? "#f1f5f9" : "black" }}>{t("Building Comparison")}</Typography>
          <Typography sx={{ ...muiStyle.typographyPageSubtitle, color: isDark ? "#94a3b8" : "#4b5563" }}>
            {t("Building lifecycle assessment and sustainability metrics comparison")}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Icon icon="fe:download" />}
          onClick={handleExport}
          sx={{ textTransform: "none", borderRadius: 2, bgcolor: isDark ? "#10b981" : "#16a34a" }}>
          {t("Export Report")}
        </Button>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* --- SELECTION HUB --- */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 4,
          border: `1px solid ${isDark ? "#3f3f46" : "#e2e8f0"}`,
          borderRadius: 4,
          bgcolor: isDark ? "#1c1c1e" : "white",
        }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={5}>
            <Typography sx={{ ...muiStyle.typographyPageSubtitle, mb: 1, fontSize: 13, fontWeight: 700, color: "#16a34a", textTransform: "uppercase" }}>
              {t("Primary Baseline")}
            </Typography>
            <Select
              options={allBuildings}
              value={allBuildings.find((b) => b.value === primaryId)}
              onChange={(opt) => setPrimaryId(opt?.value)}
              placeholder={t("Set reference building...")}
              styles={{
                control: (base) => ({
                  ...base,
                  backgroundColor: isDark ? "#27272a" : "white",
                  borderColor: isDark ? "#3f3f46" : "#e2e8f0",
                  color: isDark ? "#f8fafc" : "#0f172a",
                }),
                menu: (base) => ({
                  ...base,
                  backgroundColor: isDark ? "#27272a" : "white",
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isFocused ? (isDark ? "#3f3f46" : "#f1f5f9") : "transparent",
                  color: isDark ? "#f8fafc" : "#0f172a",
                }),
                singleValue: (base) => ({
                  ...base,
                  color: isDark ? "#f8fafc" : "#0f172a",
                }),
                placeholder: (base) => ({
                  ...base,
                  color: isDark ? "#94a3b8" : "#64748b",
                }),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2} sx={{ textAlign: "center" }}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: isDark ? "#71717a" : "#94a3b8", mt: 3 }}>VS</Typography>
          </Grid>
          <Grid item xs={12} md={5}>
            <Typography sx={{ ...muiStyle.typographyPageSubtitle, mb: 1, fontSize: 13, fontWeight: 700, color: "#3b82f6", textTransform: "uppercase" }}>
              {t("Comparison Benchmarks")}
            </Typography>
            <Select
              isMulti
              options={allBuildings.filter((b) => b.value !== primaryId)}
              value={allBuildings.filter((b) => secondaryIds.includes(b.value))}
              onChange={(opts) => setSecondaryIds(opts.map((o) => o.value))}
              placeholder={t("Add targets...")}
              styles={{
                control: (base) => ({
                  ...base,
                  backgroundColor: isDark ? "#27272a" : "white",
                  borderColor: isDark ? "#3f3f46" : "#e2e8f0",
                  color: isDark ? "#f8fafc" : "#0f172a",
                }),
                menu: (base) => ({
                  ...base,
                  backgroundColor: isDark ? "#27272a" : "white",
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isFocused ? (isDark ? "#3f3f46" : "#f1f5f9") : "transparent",
                  color: isDark ? "#f8fafc" : "#0f172a",
                }),
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: isDark ? "#3f3f46" : "#f1f5f9",
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  color: isDark ? "#f8fafc" : "#0f172a",
                }),
                placeholder: (base) => ({
                  ...base,
                  color: isDark ? "#94a3b8" : "#64748b",
                }),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {!primaryId ? (
        <Box sx={{ p: 10, textAlign: "center", border: "2px dashed #e2e8f0", borderRadius: 4 }}>
          <Icon icon="carbon:compare" width="60" style={{ color: "#cbd5e1", marginBottom: "16px" }} />
          <Typography sx={{ color: "#64748b", fontWeight: 600 }}>{t("Select a baseline building to reveal comparative insights")}</Typography>
        </Box>
      ) : processedMetrics.length === 0 ? (
        <Box sx={{ p: 10, textAlign: "center" }}>
          <Typography sx={{ color: "#64748b", mt: 2 }}>{t("Collecting benchmark data...")}</Typography>
        </Box>
      ) : (
        <>
          {/* --- SMART INSIGHTS BOX --- */}
          {smartInsights.length > 0 && (
            <Box sx={{ mb: 4, p: 3, borderRadius: 4, border: "1px solid #e0f2fe", bgcolor: "#f0f9ff", display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <Icon icon="lucide:sparkles" color="#0284c7" width="20" />
                <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 14, fontWeight: 800, color: "#0369a1" }}>{t("INTELLIGENT SUMMARY")}</Typography>
              </Box>
              {smartInsights.map((ins, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "start", gap: 1.5 }}>
                  <Box sx={{ mt: 0.5 }}>
                    <Icon icon={ins.icon} color={ins.type === "success" ? "#16a34a" : ins.type === "warning" ? "#f59e0b" : "#3b82f6"} width="16" />
                  </Box>
                  <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 13, color: "#334155", fontWeight: 500 }}>{ins.text}</Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* --- 1. PERFORMANCE SNAPSHOT (Collapsible Executive Summary) --- */}
          <SectionAccordion title={t("Executive Summary")}>
            <Grid container spacing={3} sx={{ width: "100%", m: 0 }}>
              {processedMetrics.map((m, i) => {
                const isPrimary = i === 0;
                const diff = !isPrimary ? Math.round(((m.intensity - processedMetrics[0].intensity) / processedMetrics[0].intensity) * 100) : 0;
                const score = calculateVrttaScore(m, processedMetrics[0]);

                return (
                  <Grid item xs={12} md key={m.id} sx={{ display: "flex", flexGrow: 1 }}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        width: "100%",
                        flex: 1,
                        border: `2px solid ${isPrimary ? "#10b981" : isDark ? "#3f3f46" : "#e2e8f0"}`,
                        borderRadius: 4,
                        bgcolor: isDark ? "#1c1c1e" : "white",
                        boxShadow: isPrimary ? (isDark ? "0 10px 25px rgba(16, 185, 129, 0.2)" : "0 10px 25px rgba(16, 185, 129, 0.1)") : "none",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}>
                      <Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                          <Box>
                            <Typography sx={{ ...muiStyle.typographyPageTitle, fontSize: 20, mb: 0.5, color: isDark ? "#f1f5f9" : "black" }}>{m.name}</Typography>
                            <Typography sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 12, color: isDark ? "#94a3b8" : "#94a3b8" }}>
                              {m.year} • {m.area.toLocaleString()} m²
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: "right" }}>
                            <Typography sx={{ fontSize: 10, fontWeight: 900, color: isDark ? "#71717a" : "#94a3b8" }}>VRTTA SCORE</Typography>
                            <Typography sx={{ fontSize: 24, fontWeight: 900, color: score > 70 ? "#16a34a" : score > 40 ? "#f59e0b" : "#ef4444" }}>{score}</Typography>
                          </Box>
                        </Box>

                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography
                              sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 11, fontWeight: 700, color: isDark ? "#a1a1aa" : "#64748b", textTransform: "uppercase" }}>
                              {t("Total Carbon")}
                            </Typography>
                            <Typography sx={{ ...muiStyle.typographyPageTitle, fontSize: 24, color: isDark ? "#f8fafc" : "inherit" }}>{formatValueRaw(m.gwp)}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography
                              sx={{ ...muiStyle.typographyPageSubtitle, fontSize: 11, fontWeight: 700, color: isDark ? "#a1a1aa" : "#64748b", textTransform: "uppercase" }}>
                              {t("Intensity")}
                            </Typography>
                            <Typography sx={{ ...muiStyle.typographyPageTitle, fontSize: 24, color: isDark ? "#f8fafc" : "inherit" }}>
                              {Number(m.intensity).toFixed(1)} <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 500 }}>kgCO₂e/m²</span>
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>

                      <Box sx={{ mt: 3 }}>
                        {!isPrimary ? (
                          <>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                              <Typography sx={{ fontSize: 12, fontWeight: 700, color: isDark ? "#a1a1aa" : "#64748b" }}>{t("Benchmark Variance")}</Typography>
                              <Typography sx={{ fontSize: 12, fontWeight: 800, color: diff > 0 ? "#ef4444" : "#16a34a" }}>
                                {diff > 0 ? `+${diff}% ${t("Higher")}` : `${Math.abs(diff)}% ${t("Savings")}`}
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(Math.abs(diff), 100)}
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                bgcolor: isDark ? "#27272a" : "#f1f5f9",
                                "& .MuiLinearProgress-bar": { bgcolor: diff > 0 ? "#ef4444" : "#16a34a" },
                              }}
                            />
                          </>
                        ) : (
                          <Chip
                            label={t("PRIMARY BASELINE")}
                            size="small"
                            sx={{ fontWeight: 800, bgcolor: isDark ? "rgba(16, 185, 129, 0.2)" : "#dcfce7", color: "#16a34a", borderRadius: 1 }}
                          />
                        )}
                      </Box>

                      <Box sx={{ mt: 2 }}>
                        <Button
                          fullWidth
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setShowDataAudit(showDataAudit === m.id ? null : m.id);
                          }}
                          sx={{
                            py: 1,
                            fontSize: 9,
                            fontWeight: 900,
                            color: "#10b981",
                            borderColor: "#10b981",
                            letterSpacing: 1,
                            "&:hover": { bgcolor: "#f0fdf4", borderColor: "#059669" },
                          }}>
                          {showDataAudit === m.id ? t("HIDE DATA SOURCE") : t("VIEW DATA SOURCE")}
                        </Button>
                      </Box>

                      {showTrace === m.id && <AuditTrace data={calculateVrttaScore(m, processedMetrics[0], true)} />}

                      {showDataAudit === m.id && <DataSourceAudit m={m} baseline={processedMetrics[0]} />}
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </SectionAccordion>

          {/* --- CINEMATIC BUILDING SHOWCASE (NEW COMPONENT) --- */}
          <Box sx={{ mb: 4, mt: 4, position: "relative" }}>
            <Typography
              sx={{
                ...muiStyle.typographyPageSubtitle,
                fontSize: 14,
                fontWeight: 900,
                color: isDark ? "#f1f5f9" : "#1e293b",
                mb: 3,
                textAlign: "center",
                textTransform: "uppercase",
                letterSpacing: 2,
              }}>
              {t("Cinematic Comparison Spotlight")}
            </Typography>

            <CinematicShowcase metrics={processedMetrics} calculateVrttaScore={calculateVrttaScore} />
          </Box>

          {/* --- 2. CARBON EFFICIENCY RADAR --- */}
          <Paper elevation={0} sx={{ p: 4, mb: 4, border: `1px solid ${isDark ? "#3f3f46" : "#e2e8f0"}`, borderRadius: 4, bgcolor: isDark ? "#1c1c1e" : "white" }}>
            <SectionHeader title={t("Carbon Efficiency Radar")} subtitle={t("Relative performance normalization (100 = Highest impact in group)")} />
            <Box sx={{ height: 420, width: "100%" }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} margin={{ top: 20, right: 60, bottom: 20, left: 60 }}>
                  <PolarGrid stroke={isDark ? "#3f3f46" : "#e2e8f0"} />
                  <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11, fill: isDark ? "#a1a1aa" : "#64748b", fontWeight: 700 }} />
                  <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 10, fill: isDark ? "#71717a" : "#94a3b8" }} axisLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  {processedMetrics.map((m) => (
                    <Radar key={m.id} name={m.name} dataKey={m.name} stroke={m.color} fill={m.color} fillOpacity={0.25} strokeWidth={2.5} />
                  ))}
                  <Legend wrapperStyle={{ paddingTop: 30, fontWeight: 600 }} />
                </RadarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* --- 3. CARBON GAP ANALYSIS --- */}
          <Paper elevation={0} sx={{ p: 4, mb: 4, border: `1px solid ${isDark ? "#3f3f46" : "#e2e8f0"}`, borderRadius: 4, bgcolor: isDark ? "#1c1c1e" : "white" }}>
            <SectionHeader title={t("Carbon Gap Analysis")} subtitle={t("Stage-by-stage emission variance compared to baseline")} />
            <Box sx={{ height: 420, width: "100%" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={varianceData} margin={{ top: 20, right: 30, left: 60, bottom: 40 }} stackOffset="sign">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? "#27272a" : "#f1f5f9"} />
                  <ReferenceLine y={0} stroke={isDark ? "#3f3f46" : "#cbd5e1"} strokeWidth={2} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: isDark ? "#a1a1aa" : "#64748b", fontWeight: 700 }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={(val) => formatValueRaw(val)} tick={{ fontSize: 11, fill: isDark ? "#a1a1aa" : "#64748b" }} axisLine={false} tickLine={false}>
                    <Label value="Baseline Variance" angle={-90} position="insideLeft" style={{ textAnchor: "middle", fill: isDark ? "#a1a1aa" : "#64748b", fontWeight: 700 }} />
                  </YAxis>
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: isDark ? "#27272a" : "#f8fafc" }} />
                  <Legend wrapperStyle={{ paddingTop: 20, fontWeight: 700 }} />
                  {processedMetrics.slice(1).map((m) => (
                    <Bar key={m.id} dataKey={m.name} fill={m.color} radius={[6, 6, 0, 0]} barSize={40} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* --- 4. HOTSPOT CONCENTRATION AUDIT (NEW ANALYTICAL COMPONENT) --- */}
          <Paper elevation={0} sx={{ p: 4, mb: 4, border: `1px solid ${isDark ? "#3f3f46" : "#e2e8f0"}`, borderRadius: 4, bgcolor: isDark ? "#1c1c1e" : "white" }}>
            <SectionHeader title={t("Hotspot Concentration Audit")} subtitle={t("Pareto curve of cumulative material impact. Steeper curves indicate higher optimization potential.")} />
            <Box sx={{ height: 450, width: "100%", mb: 6 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={paretoData} margin={{ top: 20, right: 30, left: 40, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? "#27272a" : "#f1f5f9"} />
                  <XAxis dataKey="index" tick={{ fontSize: 11, fill: isDark ? "#a1a1aa" : "#64748b" }}>
                    <Label
                      value="Material Rank (Rank 1 = Highest Impact)"
                      offset={-20}
                      position="insideBottom"
                      fill={isDark ? "#a1a1aa" : "#64748b"}
                      style={{ fontSize: 12, fontWeight: 700 }}
                    />
                  </XAxis>
                  <YAxis unit="%" domain={[0, 100]} tick={{ fontSize: 11, fill: isDark ? "#a1a1aa" : "#64748b" }} axisLine={false} tickLine={false}>
                    <Label
                      value="Cumulative Impact (%)"
                      angle={-90}
                      position="insideLeft"
                      style={{ textAnchor: "middle", fill: isDark ? "#a1a1aa" : "#64748b", fontWeight: 700 }}
                    />
                  </YAxis>
                  <Tooltip content={<CustomTooltip />} />
                  <ReferenceLine y={80} stroke="#ef4444" strokeDasharray="5 5" strokeWidth={2}>
                    <Label value="80% Threshold" position="insideTopRight" fill="#ef4444" style={{ fontSize: 10, fontWeight: 800 }} />
                  </ReferenceLine>
                  {processedMetrics.map((m) => (
                    <Line
                      key={m.id}
                      type="monotone"
                      dataKey={m.name}
                      stroke={m.color}
                      strokeWidth={4}
                      dot={{ r: 4, fill: m.color, strokeWidth: 2, stroke: isDark ? "#1c1c1e" : "#fff" }}
                      activeDot={{ r: 8 }}
                    />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
            </Box>

            <Box sx={{ p: 4, bgcolor: isDark ? "#27272a" : "#f8fafc", borderRadius: 4, border: `1px solid ${isDark ? "#3f3f46" : "#f1f5f9"}` }}>
              <Typography sx={{ fontSize: 11, fontWeight: 900, color: isDark ? "#94a3b8" : "#475569", mb: 2, textTransform: "uppercase", letterSpacing: 1 }}>
                {t("Optimization Strategy")}
              </Typography>
              <Typography sx={{ fontSize: 13, color: isDark ? "#a1a1aa" : "#64748b", mb: 4, lineHeight: 1.6, maxWidth: 800 }}>
                {t("The")} <span style={{ fontWeight: 800, color: isDark ? "#f1f5f9" : "#1e293b" }}>{t("80% Burden Rule")}</span>{t(": High concentration indicates that swapping just a few materials will yield maximum carbon savings.")}
              </Typography>

              <Grid container spacing={3}>
                {paretoInsights.map((ins, i) => (
                  <Grid item xs={12} md={6} lg={4} key={i}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        border: `1px solid ${isDark ? "#3f3f46" : "#e2e8f0"}`,
                        bgcolor: isDark ? "#1c1c1e" : "white",
                        transition: "transform 0.2s",
                        "&:hover": { transform: "translateY(-4px)", boxShadow: isDark ? "0 4px 12px rgba(0,0,0,0.4)" : "0 4px 12px rgba(0,0,0,0.05)" },
                      }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "start", mb: 2 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: ins.color }}>{ins.name}</Typography>
                        <Chip
                          label={ins.count80 <= 5 ? t("HIGH CONCENTRATION") : t("DISTRIBUTED")}
                          size="small"
                          sx={{
                            fontSize: 9,
                            height: 20,
                            fontWeight: 900,
                            bgcolor: ins.count80 <= 5 ? (isDark ? "rgba(245, 158, 11, 0.1)" : "#fff7ed") : isDark ? "rgba(22, 163, 74, 0.1)" : "#f0fdf4",
                            color: ins.count80 <= 5 ? "#f59e0b" : "#16a34a",
                            border: `1px solid ${ins.count80 <= 5 ? (isDark ? "#78350f" : "#ffedd5") : isDark ? "#14532d" : "#dcfce7"}`,
                          }}
                        />
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Typography sx={{ fontSize: 36, fontWeight: 900, color: isDark ? "#f8fafc" : "#1e293b" }}>{ins.count80}</Typography>
                        <Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: isDark ? "#a1a1aa" : "#475569", lineHeight: 1 }}>{t("Materials")}</Typography>
                          <Typography sx={{ fontSize: 11, color: isDark ? "#71717a" : "#94a3b8" }}>{t("make up 80% impact")}</Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>

          {/* --- 5. SECTIONAL COMPARISON --- */}
          <Paper elevation={0} sx={{ p: 4, mb: 4, border: `1px solid ${isDark ? "#3f3f46" : "#e2e8f0"}`, borderRadius: 4, bgcolor: isDark ? "#1c1c1e" : "white" }}>
            <SectionHeader title={t("Structural Impact Breakdown")} subtitle={t("Side-by-side comparison of building construction elements")} />
            <Box sx={{ height: 400, width: "100%" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sectionalData} margin={{ top: 20, right: 30, left: 60, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? "#27272a" : "#f1f5f9"} />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: isDark ? "#a1a1aa" : "#64748b", fontWeight: 700 }}
                    axisLine={false}
                    tickLine={false}
                    angle={-25}
                    textAnchor="end">
                    <Label
                      value="Building Structural Sections"
                      offset={-50}
                      position="insideBottom"
                      fill={isDark ? "#a1a1aa" : "#64748b"}
                      style={{ fontSize: 12, fontWeight: 700 }}
                    />
                  </XAxis>
                  <YAxis tickFormatter={(val) => formatValueRaw(val)} tick={{ fontSize: 11, fill: isDark ? "#a1a1aa" : "#64748b" }} axisLine={false} tickLine={false}>
                    <Label
                      value="Carbon Impact (tCO₂e)"
                      angle={-90}
                      position="insideLeft"
                      style={{ textAnchor: "middle", fill: isDark ? "#a1a1aa" : "#64748b", fontWeight: 700 }}
                    />
                  </YAxis>
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: isDark ? "#27272a" : "#f8fafc" }} />
                  <Legend verticalAlign="top" wrapperStyle={{ paddingBottom: 20, fontWeight: 700 }} />
                  {processedMetrics.map((m) => (
                    <Bar key={m.id} dataKey={m.name} fill={m.color} radius={[4, 4, 0, 0]} barSize={processedMetrics.length === 2 ? 50 : 30} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* --- 5. MATERIAL IMPACT DRIVERS --- */}
          <Paper elevation={0} sx={{ p: 4, mb: 4, border: `1px solid ${isDark ? "#3f3f46" : "#e2e8f0"}`, borderRadius: 4, bgcolor: isDark ? "#1c1c1e" : "white" }}>
            <SectionHeader title={t("Material Carbon Comparison")} subtitle={t("Direct impact analysis of top high-emission materials")} />
            <Box sx={{ height: 600, width: "100%" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={materialData} layout="vertical" margin={{ top: 5, right: 100, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={isDark ? "#27272a" : "#f1f5f9"} />
                  <XAxis type="number" tickFormatter={(val) => formatValueRaw(val)} tick={{ fontSize: 11, fill: isDark ? "#a1a1aa" : "#64748b" }}>
                    <Label value="Carbon Impact ($kgCO_2e$)" offset={-20} position="insideBottom" fill={isDark ? "#a1a1aa" : "#64748b"} style={{ fontSize: 12, fontWeight: 700 }} />
                  </XAxis>
                  <YAxis dataKey="name" type="category" width={180} tick={{ fontSize: 11, fill: isDark ? "#e4e4e7" : "#1e293b", fontWeight: 600 }}>
                    <Label
                      value="Individual Material Elements"
                      angle={-90}
                      position="insideLeft"
                      style={{ textAnchor: "middle", fill: isDark ? "#a1a1aa" : "#64748b", fontWeight: 700 }}
                    />
                  </YAxis>
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: isDark ? "#27272a" : "#f8fafc" }} />
                  <Legend wrapperStyle={{ paddingTop: 30, fontWeight: 700 }} />
                  {processedMetrics.map((m) => (
                    <Bar key={m.id} dataKey={m.name} fill={m.color} barSize={12} radius={[0, 6, 6, 0]}>
                      <LabelList
                        dataKey={`${m.name}_val`}
                        position="right"
                        formatter={(v) => (v === "0 kg" ? "" : v)}
                        style={{ fontSize: 11, fill: isDark ? "#f8fafc" : "#1e293b", fontWeight: 700 }}
                      />
                      <LabelList
                        dataKey={`${m.name}_pct`}
                        position="insideRight"
                        formatter={(v) => (v > 1 ? `${v}%` : "")}
                        style={{ fontSize: 10, fill: "white", fontWeight: 700 }}
                      />
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* --- 6. SUMMARY MATRIX --- */}
          <Paper elevation={0} sx={{ p: 4, border: `1px solid ${isDark ? "#3f3f46" : "#e2e8f0"}`, borderRadius: 4, overflow: "hidden", bgcolor: isDark ? "#1c1c1e" : "white" }}>
            <SectionHeader title={t("Comparative Matrix")} subtitle={t("Detailed performance benchmarks")} />
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: isDark ? "#27272a" : "#f8fafc" }}>
                    <TableCell sx={{ fontSize: 12, fontWeight: 900, color: isDark ? "#a1a1aa" : "#64748b", borderBottomColor: isDark ? "#3f3f46" : "#f1f5f9" }}>{t("ANALYTIC")}</TableCell>
                    {processedMetrics.map((m) => (
                      <TableCell key={m.id} align="right" sx={{ fontSize: 12, fontWeight: 900, color: m.color, borderBottomColor: isDark ? "#3f3f46" : "#f1f5f9" }}>
                        {m.name}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    { label: "Total Carbon", key: "gwp" },
                    { label: "Intensity (kgCO2e/m²)", key: "intensity" },
                    { label: "Embodied", key: "embodied" },
                    { label: "Operational", key: "operational" },
                    { label: "Analyzed Area (m²)", key: "area" },
                  ].map((row) => (
                    <TableRow key={row.key} hover sx={{ "&:hover": { bgcolor: isDark ? "#27272a" : "#f8fafc" } }}>
                      <TableCell sx={{ fontSize: 13, fontWeight: 600, color: isDark ? "#e4e4e7" : "#334155", borderBottomColor: isDark ? "#3f3f46" : "#f1f5f9" }}>
                        {t(row.label)}
                      </TableCell>
                      {processedMetrics.map((m) => (
                        <TableCell
                          key={m.id}
                          align="right"
                          sx={{ fontSize: 14, fontWeight: 800, color: isDark ? "#f8fafc" : "#1e293b", borderBottomColor: isDark ? "#3f3f46" : "#f1f5f9" }}>
                          {row.key === "intensity" ? Number(m[row.key]).toFixed(1) : row.key === "area" ? m[row.key].toLocaleString() : formatValueRaw(m[row.key])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
}

export default BuildingComparison;
